import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { publicApi } from '@/lib/api';
import { enrichHours, formatHoursSummary } from '@/lib/hours';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';

export default function Footer() {
  const [hoursSummary, setHoursSummary] = useState('Mar-Sam 11h30-14h30 / 19h-22h30');
  const [settings, setSettings] = useState({});
  const { t } = useLanguage();

  useEffect(() => {
    publicApi.getHours()
      .then(r => setHoursSummary(formatHoursSummary(r.data.hours)))
      .catch(() => {});
    publicApi.getSettings()
      .then(r => setSettings(r.data || {}))
      .catch(() => {});
  }, []);

  const navLinks = [
    { href: '/', label: t('nav.home') },
    { href: '/carte', label: t('footer.ourMenu') },
    { href: '/galerie', label: t('nav.gallery') },
    { href: '/avis', label: t('footer.customerReviews') },
    { href: '/reservation', label: t('nav.reservation') },
    { href: '/mentions-legales', label: t('footer.legalNotice') },
  ];

  return (
    <footer className="bg-garden-800 text-white relative overflow-hidden" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            {settings.restaurant_logo_url ? (
              <img
                src={settings.restaurant_logo_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${settings.restaurant_logo_url}` : settings.restaurant_logo_url}
                alt={settings.name || 'Cote Terrasse'}
                className="h-12 w-auto object-contain mb-4"
                data-testid="footer-logo-img"
              />
            ) : (
              <h3 className="font-accent text-3xl text-sunny mb-4">{settings.name || 'Cote Terrasse'}</h3>
            )}
            <p className="text-garden-300 text-sm leading-relaxed">{t('footer.description')}</p>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">{t('footer.navigation')}</h4>
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link key={link.href} to={link.href} className="text-garden-300 hover:text-sunny transition-colors text-sm">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-lg mb-4">{t('footer.contact')}</h4>
            <div className="flex flex-col gap-3 text-sm">
              <a href="https://www.google.com/maps/place/198+Cours+de+l'Argonne,+33000+Bordeaux" target="_blank" rel="noreferrer" className="flex items-start gap-2 text-garden-300 hover:text-sunny transition-colors">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                <span>198 Cours de l'Argonne, 33000 Bordeaux</span>
              </a>
              <a href="tel:0952675289" className="flex items-center gap-2 text-garden-300 hover:text-sunny transition-colors">
                <Phone size={16} className="shrink-0" />
                <span>09 52 67 52 89</span>
              </a>
              <div className="flex items-center gap-2 text-garden-300" data-testid="footer-hours">
                <Clock size={16} className="shrink-0" />
                <span>{hoursSummary}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-garden-700 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-garden-400 text-xs">&copy; {new Date().getFullYear()} Cote Terrasse. {t('footer.copyright')}</p>
          <div className="flex items-center gap-4">
            <Link to="/mentions-legales" className="text-garden-400 text-xs hover:text-sunny transition-colors">
              {t('footer.legalAndPrivacy')}
            </Link>
            <Link to="/admin" className="text-garden-600/40 text-[10px] hover:text-garden-400 transition-colors" data-testid="footer-admin-link">
              {t('footer.admin')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
